import React from "react";
import YouTube from "react-youtube";

// YouTube player states (YT.PlayerState).
const ENDED = 0;
const PLAYING = 1;
const BUFFERING = 3;

/**
 * YouTube media for a swipe-deck embed, built on `react-youtube`.
 *
 * Why react-youtube instead of attaching our own SDK to a hand-rendered iframe:
 * the library lets the YouTube player OWN its iframe (it creates it from the
 * video id), which is the only pattern that reliably runs several players at
 * once and tears them down safely. Our previous "new YT.Player(existingIframe)"
 * approach corrupted the API's shared state past two simultaneous players, so a
 * third embed in a deck would hang on a spinner. This component is rendered for
 * the active card AND its neighbors (preload), so swiping stays instant.
 *
 * It reports playback through `onProgress`/`onEnded` and exposes imperative
 * controls (play/pause/isPlaying) via `controllerRef` so the parent card can
 * drive the gesture overlay + read-more sheet. Mute is the deck-shared state.
 */
export function YouTubeEmbedMedia({
  videoId,
  startSeconds,
  isActive,
  muted,
  onProgress,
  onEnded,
  controllerRef,
}) {
  const playerRef = React.useRef(null);
  const isPlayingRef = React.useRef(false);
  // True once the clip ended — keeps the progress poll from clobbering the
  // snapped-to-full bar, and tells play() to reload rather than resume.
  const endedRef = React.useRef(false);
  // Latest props for the async player callbacks.
  const stateRef = React.useRef({ isActive, muted });
  stateRef.current = { isActive, muted };
  const onProgressRef = React.useRef(onProgress);
  onProgressRef.current = onProgress;
  const onEndedRef = React.useRef(onEnded);
  onEndedRef.current = onEnded;

  // Start/resume honoring the deck's mute state (a swipe is a user gesture, so
  // unmuted playback is allowed; if a browser blocks it the card just pauses on
  // a frame and a tap resumes it).
  const ensurePlay = (player) => {
    if (!player) return;
    try {
      if (stateRef.current.muted) player.mute();
      else player.unMute();
      player.playVideo();
    } catch (e) {
      /* not ready */
    }
  };

  // Reliable restart of an ENDED clip — playVideo() alone hangs, loadVideoById
  // reloads cleanly.
  const replay = (player) => {
    if (!player) return;
    endedRef.current = false;
    try {
      const data = player.getVideoData && player.getVideoData();
      const id = (data && data.video_id) || videoId;
      if (id) player.loadVideoById(id);
      else player.playVideo();
      if (stateRef.current.muted) player.mute();
      else player.unMute();
    } catch (e) {
      /* not ready */
    }
  };

  // Publish the imperative controller for the parent (gesture overlay +
  // read-more sheet). Set each render so it always reads live refs.
  if (controllerRef) {
    controllerRef.current = {
      isPlaying: () => isPlayingRef.current,
      pause: () => {
        try {
          playerRef.current && playerRef.current.pauseVideo();
        } catch (e) {
          /* not ready */
        }
      },
      play: () => {
        const p = playerRef.current;
        if (!p) return;
        if (endedRef.current) replay(p);
        else {
          try {
            p.playVideo();
          } catch (e) {
            /* not ready */
          }
        }
      },
    };
  }

  // Play only the active card; pause the rest. (Neighbors are preloaded but
  // paused, so swiping to them is instant.)
  React.useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    if (isActive) ensurePlay(p);
    else {
      try {
        p.pauseVideo();
      } catch (e) {
        /* not ready */
      }
    }
    // ensurePlay reads live state via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Keep the player's mute in sync with the deck-shared muted state.
  React.useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (muted) p.mute();
      else p.unMute();
    } catch (e) {
      /* not ready */
    }
  }, [muted]);

  // YouTube has no timeupdate event, so poll the active card's position to drive
  // the deck's top progress bar.
  React.useEffect(() => {
    const id = setInterval(() => {
      const p = playerRef.current;
      if (
        !p ||
        endedRef.current ||
        !stateRef.current.isActive ||
        typeof p.getDuration !== "function"
      ) {
        return;
      }
      try {
        const dur = p.getDuration();
        const cur = p.getCurrentTime();
        if (dur > 0 && onProgressRef.current) {
          onProgressRef.current(Math.min(1, cur / dur));
        }
      } catch (e) {
        /* not ready */
      }
    }, 400);
    return () => clearInterval(id);
  }, []);

  const opts = React.useMemo(
    () => ({
      width: "100%",
      height: "100%",
      host: "https://www.youtube-nocookie.com",
      playerVars: {
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        controls: 0,
        iv_load_policy: 3,
        // Start muted to satisfy autoplay policy; we unMute via the API when the
        // deck is unmuted and we have a gesture.
        mute: 1,
        ...(startSeconds ? { start: startSeconds } : {}),
        ...(typeof window !== "undefined"
          ? { origin: window.location.origin }
          : {}),
      },
    }),
    [startSeconds]
  );

  const handleReady = (event) => {
    playerRef.current = event.target;
    try {
      if (stateRef.current.muted) event.target.mute();
      else event.target.unMute();
    } catch (e) {
      /* not ready */
    }
    if (stateRef.current.isActive) ensurePlay(event.target);
  };

  const handleStateChange = (event) => {
    if (event.data === ENDED) {
      endedRef.current = true;
      onProgressRef.current && onProgressRef.current(1);
      onEndedRef.current && onEndedRef.current();
    } else if (event.data === PLAYING || event.data === BUFFERING) {
      endedRef.current = false;
    }
    isPlayingRef.current = event.data === PLAYING;
  };

  return (
    <YouTube
      videoId={videoId}
      className="absolute inset-0 w-full h-full"
      iframeClassName="absolute inset-0 w-full h-full"
      title="Video"
      opts={opts}
      onReady={handleReady}
      onStateChange={handleStateChange}
    />
  );
}
